import type { CreateWaitlistBody, WaitlistPurpose } from './contract.js';
import { OTHER_PURPOSE, WAITLIST_MAX_PURPOSES } from './contract.js';
import { type CSSProperties, type FormEvent, useEffect, useRef, useState } from 'react';
import { FEELINGS, WAITLIST } from './content.js';
import { FeelingIcon } from './icons.js';
import { ApiError, post } from './lib/api.js';

type Field = 'name' | 'email' | 'mobile' | 'purposes' | 'otherInterest';

/**
 * The waitlist, as a modal.
 *
 * A native <dialog> rather than a hand-rolled overlay: Escape to close, focus kept inside,
 * the rest of the page marked inert and a real ::backdrop all come from the platform. The
 * cost is that the element has its own open/closed state, so it is driven imperatively from
 * the `open` prop below and reports closes back through onClose.
 *
 * Validation stays thin — the server owns the rules, and its Zod issues are mapped back onto
 * the fields they came from. Duplicating the email and mobile rules here would just mean two
 * definitions to keep in step.
 */
export function Waitlist({
  open,
  onClose,
  initialPurpose,
}: {
  open: boolean;
  onClose: () => void;
  /** Set when someone opened this from a feeling card, so the form starts answered. */
  initialPurpose?: string | undefined;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState('');
  const [purposes, setPurposes] = useState<WaitlistPurpose[]>([]);
  const [otherInterest, setOtherInterest] = useState('');
  const [company, setCompany] = useState(''); // honeypot — real people never see this
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      // Reopening after a successful signup should be a fresh form, not a stale receipt.
      setDone(false);
      setErrors({});
      setFailure(null);
      setPurposes(
        initialPurpose && FEELINGS.some((f) => f.key === initialPurpose)
          ? [initialPurpose as WaitlistPurpose]
          : [],
      );
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, initialPurpose]);

  const atLimit = purposes.length >= WAITLIST_MAX_PURPOSES;
  const wantsOther = purposes.includes(OTHER_PURPOSE);

  function toggle(key: WaitlistPurpose) {
    setPurposes((current) =>
      current.includes(key)
        ? current.filter((value) => value !== key)
        : current.length < WAITLIST_MAX_PURPOSES
          ? [...current, key]
          : current,
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFailure(null);

    if (purposes.length === 0) {
      setErrors({ purposes: 'Pick at least one — it tells us what to build first.' });
      return;
    }

    const body: CreateWaitlistBody = {
      name: name.trim(),
      email: email.trim(),
      mobile: `+91${digits}`,
      purposes,
      ...(wantsOther ? { otherInterest: otherInterest.trim() } : {}),
      source: 'website',
      ...(company ? { company } : {}),
    };

    setBusy(true);
    try {
      await post<{ ok: true }>('/waitlist', body);
      setDone(true);
    } catch (caught) {
      if (caught instanceof ApiError && caught.issues.length > 0) {
        const mapped: Partial<Record<Field, string>> = {};
        for (const issue of caught.issues) {
          const field = issue.path.split('.')[0] as Field;
          mapped[field] ??= issue.message;
        }
        setErrors(mapped);
      } else {
        setFailure(caught instanceof Error ? caught.message : 'Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    // A native <dialog> already closes on Escape, and the handler below only fires for a
    // click landing on the element itself — the backdrop — which no keyboard interaction can
    // target. A key handler here would be dead code that reads like an accessibility feature.
    // The directive has to be the LAST comment before the node, or biome ignores it.
    // biome-ignore lint/a11y/useKeyWithClickEvents: the backdrop is not keyboard-reachable
    <dialog
      className="modal"
      ref={ref}
      onClose={onClose}
      // Clicking the backdrop lands on the dialog itself, never on its contents.
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="modal-panel">
        <span className="modal-glow" aria-hidden="true" />
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        {done ? (
          <div className="modal-done">
            <span className="success-mark" aria-hidden="true">
              ✓
            </span>
            <h2 className="display display-sm">{WAITLIST.success.title}</h2>
            <p>{WAITLIST.success.body}</p>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="modal-head">
              <p className="eyebrow">{WAITLIST.eyebrow}</p>
              <h2 className="display display-sm">{WAITLIST.title}</h2>
              <p className="modal-sub">{WAITLIST.body}</p>
            </div>

            <form className="form" onSubmit={submit} noValidate>
              <div className="field-row">
                <label className="field">
                  <span>Your name</span>
                  <input
                    className="input"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Kavya"
                    autoComplete="name"
                    required
                  />
                  {errors.name ? <em className="error">{errors.name}</em> : null}
                </label>

                <label className="field">
                  <span>Email</span>
                  <input
                    className="input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                  {errors.email ? <em className="error">{errors.email}</em> : null}
                </label>
              </div>

              <label className="field">
                <span>Mobile</span>
                <div className="mobile">
                  <span className="mobile-cc">+91</span>
                  <input
                    className="input"
                    value={digits}
                    onChange={(event) =>
                      setDigits(event.target.value.replace(/\D/g, '').slice(0, 10))
                    }
                    placeholder="98765 43210"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    required
                  />
                </div>
                {errors.mobile ? <em className="error">{errors.mobile}</em> : null}
              </label>

              <fieldset className="field">
                <legend>
                  What are you hoping for?{' '}
                  <span className="hint">pick up to {WAITLIST_MAX_PURPOSES}</span>
                </legend>
                {/* The nine goals as a 3x3, carrying the same tints as the page's grid. */}
                <div className="purpose-grid">
                  {FEELINGS.map((feeling) => {
                    const on = purposes.includes(feeling.key as WaitlistPurpose);
                    return (
                      <button
                        type="button"
                        key={feeling.key}
                        className={`purpose${on ? ' purpose-on' : ''}`}
                        aria-pressed={on}
                        disabled={!on && atLimit}
                        onClick={() => toggle(feeling.key as WaitlistPurpose)}
                        style={{ '--tint': feeling.tint } as CSSProperties}
                      >
                        <FeelingIcon name={feeling.key} />
                        <span className="purpose-label">{feeling.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.purposes ? <em className="error">{errors.purposes}</em> : null}
              </fieldset>

              {/* Kept apart from the nine: this one is an escape hatch, not a tenth goal. */}
              <div className="other-block">
                <button
                  type="button"
                  className={`other-toggle${wantsOther ? ' other-on' : ''}`}
                  aria-pressed={wantsOther}
                  disabled={!wantsOther && atLimit}
                  onClick={() => toggle(OTHER_PURPOSE)}
                >
                  <span className="other-plus" aria-hidden="true">
                    {wantsOther ? '✓' : '+'}
                  </span>
                  Something else
                </button>
                {wantsOther ? (
                  <label className="field other-field">
                    <span>Tell us in your words</span>
                    <textarea
                      className="input"
                      rows={2}
                      maxLength={120}
                      value={otherInterest}
                      onChange={(event) => setOtherInterest(event.target.value)}
                      placeholder="What is on your mind these days?"
                    />
                    {errors.otherInterest ? (
                      <em className="error">{errors.otherInterest}</em>
                    ) : null}
                  </label>
                ) : null}
              </div>

              {/* Honeypot. Hidden from people and from screen readers; only a bot fills it. */}
              <input
                className="hp"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={company}
                onChange={(event) => setCompany(event.target.value)}
              />

              {failure ? <p className="error error-block">{failure}</p> : null}

              <button className="btn btn-primary" type="submit" disabled={busy}>
                {busy ? 'Adding you…' : 'Join the waitlist'} <span aria-hidden="true">→</span>
              </button>
              <p className="note">We will only email you about Manasil. No sharing, no noise.</p>
            </form>
          </>
        )}
      </div>
    </dialog>
  );
}
